/**
 * Holiday Calendar (M26 PR 5) — /policies/holiday-calendar.
 *
 * Month grid + year toggle. KSA statutory holidays seeded per
 * LegalEntity by `scripts/seed_default_holidays`; HR adds company
 * holidays on top via the "+ Add" modal. Statutory rows are
 * immutable (the API returns 409 on DELETE) — the page hides delete
 * for those.
 *
 * Hijri toggle: the page displays the Hijri equivalent next to each
 * Gregorian date via Intl.DateTimeFormat with the Umm Al-Qura
 * calendar. Purely a display concern — the table stays Gregorian.
 *
 * M15 leave rules consult /api/holidays via the
 * `remove_holidays_from_transaction` flag (still wired); this page
 * is just the user-facing surface for the same data.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Loading } from "../components/Loading";
import { EmptyState, EntityDrawer, PageHeader } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type { HolidayItem } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

type SourceFilter = "all" | "statutory" | "company";

const _MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function _hijri(iso: string, locale: string): string | null {
  try {
    const fmt = new Intl.DateTimeFormat(`${locale}-u-ca-islamic-umalqura`, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return fmt.format(new Date(iso + "T00:00:00"));
  } catch {
    return null;
  }
}

function _daysInMonth(year: number, monthIdx: number): number {
  return new Date(year, monthIdx + 1, 0).getDate();
}

function _weekdayIdx(year: number, monthIdx: number, day: number): number {
  // 0 = Sunday → 6 = Saturday. Match the Western convention; the
  // grid starts Sun-aligned to keep the columns wide enough for
  // names. (KSA's working week starts Sunday too.)
  return new Date(year, monthIdx, day).getDay();
}

export function HolidayCalendarPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId: le, gate } = useLegalEntityGate();

  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [showHijri, setShowHijri] = useState<boolean>(false);
  const [view, setView] = useState<"month" | "year">("month");
  const [monthIdx, setMonthIdx] = useState<number>(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState<boolean>(false);

  const q = useQuery({
    queryKey: ["holidays", le, year, sourceFilter],
    queryFn: () =>
      api.listHolidays({
        legalEntityId: le,
        year,
        source: sourceFilter === "all" ? null : sourceFilter,
      }),
    enabled: !!le,
  });

  const byDate = useMemo(() => {
    const m = new Map<string, HolidayItem[]>();
    for (const h of q.data ?? []) {
      const prev = m.get(h.observed_date) ?? [];
      prev.push(h);
      m.set(h.observed_date, prev);
    }
    return m;
  }, [q.data]);

  const del = useMutation({
    mutationFn: (id: string) => api.deleteHoliday(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["holidays", le, year] }),
  });

  if (gate) return gate;

  const selectedHolidays = selectedDate ? byDate.get(selectedDate) ?? [] : [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.holiday_calendar")}
        subtitle={t("policies.holiday_calendar_subtitle")}
        breadcrumbs={[
          { label: t("nav.policies"), href: "/policies" },
          { label: t("nav.holiday_calendar") },
        ]}
        actions={
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90"
          >
            <Plus size={14} aria-hidden /> {t("policies.holiday_calendar_add_cta")}
          </button>
        }
      />

      {/* Controls — year stepper + month/year toggle + source pills + hijri toggle. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-surface-default p-0.5">
          <button
            type="button"
            aria-label={t("policies.holiday_calendar_prev_year")}
            onClick={() => setYear(year - 1)}
            className="rounded p-1 text-text-secondary hover:bg-surface-sunken"
          >
            <ChevronLeft size={14} className="rtl:rotate-180" />
          </button>
          <span className="px-2 text-sm font-semibold text-text-primary tabular-nums">
            {year}
          </span>
          <button
            type="button"
            aria-label={t("policies.holiday_calendar_next_year")}
            onClick={() => setYear(year + 1)}
            className="rounded p-1 text-text-secondary hover:bg-surface-sunken"
          >
            <ChevronRight size={14} className="rtl:rotate-180" />
          </button>
        </div>

        <div className="inline-flex rounded-md border border-border-subtle bg-surface-default p-0.5">
          {(["month", "year"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`rounded px-2.5 py-1 text-xs font-medium ${
                view === v
                  ? "bg-brand text-brand-fg"
                  : "text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {v === "month"
                ? t("policies.holiday_calendar_view_month")
                : t("policies.holiday_calendar_view_year")}
            </button>
          ))}
        </div>

        {view === "month" && (
          <select
            value={monthIdx}
            onChange={(e) => setMonthIdx(Number(e.target.value))}
            className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
          >
            {_MONTHS_EN.map((m, i) => (
              <option key={m} value={i}>
                {m}
              </option>
            ))}
          </select>
        )}

        <div className="flex items-center gap-1">
          {(["all", "statutory", "company"] as SourceFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSourceFilter(s)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                sourceFilter === s
                  ? "border-brand bg-brand text-brand-fg"
                  : "border-border-subtle bg-surface-default text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {s === "all"
                ? t("policies.holiday_calendar_filter_all")
                : s === "statutory"
                  ? t("policies.holiday_calendar_source_statutory")
                  : t("policies.holiday_calendar_source_company")}
            </button>
          ))}
        </div>

        <label className="ms-auto inline-flex items-center gap-1.5 text-xs text-text-secondary">
          <input
            type="checkbox"
            checked={showHijri}
            onChange={(e) => setShowHijri(e.target.checked)}
          />
          {t("policies.holiday_calendar_show_hijri")}
        </label>
      </div>

      {q.isLoading ? (
        <Loading />
      ) : (q.data ?? []).length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          headline={t("policies.holiday_calendar_empty_headline")}
          description={t("policies.holiday_calendar_empty_body")}
        />
      ) : view === "month" ? (
        <MonthGrid
          year={year}
          monthIdx={monthIdx}
          byDate={byDate}
          showHijri={showHijri}
          onSelectDate={setSelectedDate}
        />
      ) : (
        <YearGrid
          year={year}
          byDate={byDate}
          onSelectDate={(iso) => {
            setSelectedDate(iso);
            setMonthIdx(parseInt(iso.slice(5, 7), 10) - 1);
          }}
        />
      )}

      {/* Selected-date drawer — inline card on this page rather than
          an EntityDrawer, the content is small enough that opening a
          dedicated overlay is overkill. */}
      {selectedDate && selectedHolidays.length > 0 && (
        <aside
          aria-label={t("policies.holiday_calendar_details_aria")}
          className="rounded-xl border border-border-subtle bg-surface-default p-4"
        >
          <header className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-text-primary">
              {selectedDate}
              {showHijri && (
                <span className="ms-2 text-xs text-text-tertiary">
                  ({_hijri(selectedDate, "en")})
                </span>
              )}
            </h2>
            <button
              type="button"
              onClick={() => setSelectedDate(null)}
              className="text-xs text-text-tertiary hover:text-text-primary"
            >
              Close
            </button>
          </header>
          <ul className="flex flex-col gap-2">
            {selectedHolidays.map((h) => (
              <li
                key={h.id}
                className="flex items-center justify-between rounded border border-border-subtle bg-surface-sunken px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-text-primary">
                    {h.name_en}
                  </span>
                  <span className="text-xs text-text-tertiary" dir="rtl">
                    {h.name_ar}
                  </span>
                  <span
                    className={`mt-1 inline-flex w-fit items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-medium ${
                      h.source === "statutory"
                        ? "border-sky-200 bg-sky-50 text-sky-700"
                        : "border-emerald-200 bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {h.source}
                  </span>
                </div>
                {h.source === "company" ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Delete "${h.name_en}"?`)) {
                        del.mutate(h.id);
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 size={12} aria-hidden /> Delete
                  </button>
                ) : (
                  <span className="text-xs text-text-tertiary">Read-only</span>
                )}
              </li>
            ))}
          </ul>
        </aside>
      )}

      {showAdd && le && (
        <AddHolidayModal
          legalEntityId={le}
          defaultDate={selectedDate}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            qc.invalidateQueries({ queryKey: ["holidays", le, year] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month grid
// ---------------------------------------------------------------------------

function MonthGrid({
  year,
  monthIdx,
  byDate,
  showHijri,
  onSelectDate,
}: {
  year: number;
  monthIdx: number;
  byDate: Map<string, HolidayItem[]>;
  showHijri: boolean;
  onSelectDate: (iso: string) => void;
}) {
  const days = _daysInMonth(year, monthIdx);
  const startWd = _weekdayIdx(year, monthIdx, 1);
  const cells: Array<{ iso: string; day: number } | null> = [];
  for (let i = 0; i < startWd; i++) cells.push(null);
  for (let d = 1; d <= days; d++) {
    const iso = `${year}-${String(monthIdx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ iso, day: d });
  }
  // pad to a multiple of 7 for clean rows.
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-default p-3">
      <div className="grid grid-cols-7 gap-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div
            key={d}
            className="px-1 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-text-tertiary"
          >
            {d}
          </div>
        ))}
        {cells.map((c, i) =>
          c === null ? (
            <div key={i} className="h-20 rounded bg-transparent" />
          ) : (
            <DayCell
              key={c.iso}
              iso={c.iso}
              day={c.day}
              holidays={byDate.get(c.iso) ?? []}
              showHijri={showHijri}
              onClick={() => onSelectDate(c.iso)}
            />
          ),
        )}
      </div>
    </div>
  );
}

function DayCell({
  iso,
  day,
  holidays,
  showHijri,
  onClick,
}: {
  iso: string;
  day: number;
  holidays: HolidayItem[];
  showHijri: boolean;
  onClick: () => void;
}) {
  const hasStatutory = holidays.some((h) => h.source === "statutory");
  const hasCompany = holidays.some((h) => h.source === "company");
  const tone =
    hasStatutory && hasCompany
      ? "border-brand bg-brand/5"
      : hasStatutory
      ? "border-sky-200 bg-sky-50/70"
      : hasCompany
      ? "border-emerald-200 bg-emerald-50/70"
      : "border-border-subtle bg-surface-default hover:bg-surface-sunken";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-20 flex-col items-start gap-0.5 rounded border p-1.5 text-start transition-colors ${tone}`}
      aria-label={`${iso}${holidays.length ? ` — ${holidays.length} holiday${holidays.length > 1 ? "s" : ""}` : ""}`}
    >
      <span className="text-xs font-medium text-text-primary tabular-nums">
        {day}
      </span>
      {showHijri && (
        <span className="text-[10px] text-text-tertiary truncate">
          {_hijri(iso, "en")}
        </span>
      )}
      {holidays.slice(0, 2).map((h) => (
        <span
          key={h.id}
          className={`mt-auto w-full truncate rounded px-1 py-0.5 text-[10px] font-medium ${
            h.source === "statutory"
              ? "bg-sky-100 text-sky-800"
              : "bg-emerald-100 text-emerald-800"
          }`}
        >
          {h.name_en}
        </span>
      ))}
      {holidays.length > 2 && (
        <span className="text-[10px] text-text-tertiary">
          +{holidays.length - 2} more
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Year overview
// ---------------------------------------------------------------------------

function YearGrid({
  year,
  byDate,
  onSelectDate,
}: {
  year: number;
  byDate: Map<string, HolidayItem[]>;
  onSelectDate: (iso: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {_MONTHS_EN.map((name, idx) => {
        const days = _daysInMonth(year, idx);
        const monthHolidays: Array<{ iso: string; h: HolidayItem }> = [];
        for (let d = 1; d <= days; d++) {
          const iso = `${year}-${String(idx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          for (const h of byDate.get(iso) ?? []) {
            monthHolidays.push({ iso, h });
          }
        }
        return (
          <article
            key={name}
            className="rounded-xl border border-border-subtle bg-surface-default p-3"
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">
              {name}
            </h3>
            {monthHolidays.length === 0 ? (
              <p className="text-xs text-text-tertiary">{t("policies.holiday_calendar_none")}</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {monthHolidays.map(({ iso, h }) => (
                  <li
                    key={h.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <button
                      type="button"
                      onClick={() => onSelectDate(iso)}
                      className="text-start text-xs text-text-primary hover:underline"
                    >
                      <span className="font-mono text-text-tertiary me-1">
                        {iso.slice(8)}
                      </span>
                      {h.name_en}
                    </button>
                    <span
                      className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${
                        h.source === "statutory"
                          ? "border-sky-200 bg-sky-50 text-sky-700"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {h.source}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add company holiday modal
// ---------------------------------------------------------------------------

function AddHolidayModal({
  legalEntityId,
  defaultDate,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  defaultDate: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name_en: "",
    name_ar: "",
    observed_date: defaultDate ?? new Date().toISOString().slice(0, 10),
    recurrence: "one_time" as "one_time" | "annual",
    note: "",
  });
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.createHoliday({
        legal_entity_id: legalEntityId,
        name_en: form.name_en,
        name_ar: form.name_ar,
        observed_date: form.observed_date,
        recurrence: form.recurrence,
        note: form.note || null,
      }),
    onSuccess: () => onCreated(),
    onError: (e) =>
      setErr(e instanceof ApiError ? e.detail : String(e)),
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("policies.holiday_calendar_add_title")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setErr(null);
              if (!form.name_en.trim() || !form.name_ar.trim()) {
                setErr("Both EN and AR names are required.");
                return;
              }
              mut.mutate();
            }}
            disabled={mut.isPending}
            className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            {mut.isPending
              ? t("policies.saving")
              : t("policies.holiday_calendar_add_cta")}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-text-secondary">{t("policies.name_en_label")}</span>
          <input
            value={form.name_en}
            onChange={(e) => setForm({ ...form, name_en: e.target.value })}
            className="rounded border border-border-subtle px-2 py-1.5"
            placeholder="e.g. Company Anniversary"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-text-secondary">{t("policies.name_ar_label")}</span>
          <input
            value={form.name_ar}
            onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
            dir="rtl"
            className="rounded border border-border-subtle px-2 py-1.5 text-end"
            placeholder="مثال: ذكرى تأسيس الشركة"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-text-secondary">Date</span>
          <input
            type="date"
            value={form.observed_date}
            onChange={(e) =>
              setForm({ ...form, observed_date: e.target.value })
            }
            className="rounded border border-border-subtle px-2 py-1.5"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-text-secondary">Recurrence</span>
          <select
            value={form.recurrence}
            onChange={(e) =>
              setForm({
                ...form,
                recurrence: e.target.value as "annual" | "one_time",
              })
            }
            className="rounded border border-border-subtle px-2 py-1.5"
          >
            <option value="one_time">One-time</option>
            <option value="annual">Annual (recurs every year)</option>
          </select>
        </label>
        {err && <p className="text-xs text-rose-700">{err}</p>}
      </div>
    </EntityDrawer>
  );
}
